<script setup lang="ts">
import { KeyRound, Loader, Pencil, Plug, PlugZap } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { ref } from "vue";
import { toast } from "vue-sonner";
import { putConnectionProvider, registerConnectionProviderClient, testConnectionProvider, useAdminConnectionProviders, type ConnectionProviderUpdate } from "@/api/admin";
import type { AdminConnectionProvider } from "@/api/contracts";
import ConnectionProviderForm from "@/components/admin/ConnectionProviderForm.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import StatusBadge from "@/components/common/StatusBadge.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { confirm } from "@/composables/useConfirm";
import { t } from "@/copy/strings";
import { errorMessage } from "@/lib/errors";
import { READ_IDENTITY_OPTIONS } from "@/lib/providers";
import { cn } from "@/lib/utils";

definePage({ meta: { title: "Connection providers", requires: { adminArea: "connections" } } });

const queryClient = useQueryClient();
const providers = useAdminConnectionProviders();
const editing = ref<AdminConnectionProvider | null>(null);
const open = ref(false);
const busy = ref(false);
const error = ref<unknown>(null);
const testing = ref<string | null>(null);
const results = ref<Record<string, { ok: boolean; message: string }>>({});

function edit(p: AdminConnectionProvider) {
  editing.value = p;
  error.value = null;
  open.value = true;
}

async function save(update: ConnectionProviderUpdate) {
  if (!editing.value) return;
  busy.value = true;
  error.value = null;
  try {
    await putConnectionProvider(editing.value.id, update);
    toast.success(`${editing.value.label} saved.`);
    open.value = false;
    await queryClient.invalidateQueries({ queryKey: ["admin", "connection-providers"] });
    await queryClient.invalidateQueries({ queryKey: ["connections"] });
  } catch (err) {
    error.value = err;
  } finally {
    busy.value = false;
  }
}

async function test(p: AdminConnectionProvider) {
  testing.value = p.id;
  try {
    results.value = { ...results.value, [p.id]: await testConnectionProvider(p.id) };
  } catch (err) {
    results.value = { ...results.value, [p.id]: { ok: false, message: errorMessage(err) } };
  } finally {
    testing.value = null;
  }
}

const registering = ref<string | null>(null);

/** RFC 7591: register smake-mcp upstream and store the client it gets. */
async function registerClient(p: AdminConnectionProvider) {
  if (p.clientId) {
    const answer = await confirm({
      title: `Register a new ${p.label} client?`,
      description: `This replaces client ${p.clientId}. Existing connections were issued to the old client and may have to reconnect.`,
      confirmLabel: "Register client",
    });
    if (!answer) return;
  }
  registering.value = p.id;
  try {
    const saved = await registerConnectionProviderClient(p.id);
    results.value = { ...results.value, [p.id]: { ok: true, message: `Registered client ${saved.clientId}${saved.hasClientSecret ? " (with secret)" : " (public client)"}.` } };
    await queryClient.invalidateQueries({ queryKey: ["admin", "connection-providers"] });
  } catch (err) {
    results.value = { ...results.value, [p.id]: { ok: false, message: errorMessage(err) } };
  } finally {
    registering.value = null;
  }
}

const readLabel = (v: string) => READ_IDENTITY_OPTIONS.find((o) => o.value === v)?.label ?? v;
const pinText = (pin: Record<string, unknown>) =>
  Object.entries(pin).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : String(v)}`).join(" · ") || "not pinned";
</script>

<template>
  <div>
    <PageHeader :crumbs="[{ label: t.admin.title, to: '/admin' }]" :title="t.admin.connectionProviders" :description="t.admin.connectionProvidersDescription" />
    <ListSkeleton v-if="providers.isPending.value" :rows="3" />
    <ErrorState v-else-if="providers.error.value" :error="providers.error.value" @retry="providers.refetch()" />
    <EmptyState v-else-if="!providers.data.value?.length" :icon="PlugZap" title="No providers" />
    <ul v-else class="grid gap-3 lg:grid-cols-2">
      <li v-for="p in providers.data.value" :key="p.id" :class="cn('flex flex-col gap-3 rounded-xl border bg-card p-4 shadow-xs', !p.enabled && 'bg-muted/30')">
        <div class="flex items-start gap-2">
          <div class="min-w-0 flex-1">
            <p class="font-medium">{{ p.label }} <span class="font-mono text-xs text-muted-foreground">{{ p.id }}</span></p>
          </div>
          <StatusBadge :status="p.enabled ? 'connected' : 'disabled'" :label="p.enabled ? t.common.enabled : t.common.disabled" />
        </div>
        <dl class="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm sm:grid-cols-[8rem_minmax(0,1fr)]">
          <template v-if="p.kind === 'credentials'">
            <dt class="text-muted-foreground">Connect</dt>
            <dd>Each person pastes their own credentials</dd>
          </template>
          <template v-else>
            <dt class="text-muted-foreground">Client</dt>
            <dd class="truncate font-mono text-xs">{{ p.clientId ?? "—" }} <Badge v-if="p.hasClientSecret" variant="muted" class="ml-1">secret set</Badge><Badge v-else-if="p.publicClient" variant="muted" class="ml-1">public client</Badge><Badge v-else variant="warning" class="ml-1">no secret</Badge></dd>
          </template>
          <dt class="text-muted-foreground">Tenant</dt>
          <dd class="break-words">{{ pinText(p.tenantPin) }}</dd>
          <dt class="text-muted-foreground">Reads</dt>
          <dd>{{ readLabel(p.readIdentity) }}</dd>
          <dt class="text-muted-foreground">Service identity</dt>
          <dd>{{ p.serviceCredential.configured ? (p.serviceCredential.account ?? "configured") : "—" }}</dd>
          <template v-if="p.kind !== 'credentials'">
            <dt class="text-muted-foreground">Scopes</dt>
            <dd class="flex min-w-0 flex-wrap gap-1"><Badge v-for="s in p.scopes" :key="s" variant="muted" class="justify-start font-mono whitespace-normal [overflow-wrap:anywhere]">{{ s }}</Badge><span v-if="!p.scopes.length">—</span></dd>
            <template v-if="p.missingScopes?.length">
              <dt class="text-muted-foreground">Missing scopes</dt>
              <dd class="min-w-0 text-destructive">
                <span class="flex flex-wrap gap-1"><Badge v-for="s in p.missingScopes" :key="s" variant="danger" class="justify-start font-mono whitespace-normal [overflow-wrap:anywhere]">{{ s }}</Badge></span>
                <span class="mt-1 block text-xs">Tools need these, but this provider does not request them, so reconnecting cannot grant them. Add them here (and on the upstream app), then everyone reconnects.</span>
              </dd>
            </template>
          </template>
        </dl>
        <p v-if="results[p.id]" role="status" :class="cn('rounded-md px-2.5 py-1.5 text-xs', results[p.id].ok ? 'bg-success-soft text-success' : 'bg-destructive-soft text-destructive')">
          {{ results[p.id].message }}
        </p>
        <div class="mt-auto flex gap-2">
          <Button size="sm" @click="edit(p)"><Pencil /> {{ t.common.edit }}</Button>
          <Button size="sm" variant="outline" :disabled="testing === p.id" @click="test(p)">
            <Loader v-if="testing === p.id" class="animate-spin" /><Plug v-else /> {{ t.common.test }}
          </Button>
          <Button v-if="p.dynamicRegistration" size="sm" variant="outline" :disabled="registering === p.id" @click="registerClient(p)">
            <Loader v-if="registering === p.id" class="animate-spin" /><KeyRound v-else /> Register client
          </Button>
        </div>
      </li>
    </ul>

    <Sheet v-model:open="open">
      <SheetContent side="right" class="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{{ editing?.label }}</SheetTitle>
          <SheetDescription>Secrets are write-only: they are stored encrypted and never shown again.</SheetDescription>
        </SheetHeader>
        <div class="px-4 pb-6">
          <ConnectionProviderForm v-if="editing && open" :key="editing.id" :provider="editing" :busy="busy" :error="error" @submit="save" @cancel="open = false" />
        </div>
      </SheetContent>
    </Sheet>
  </div>
</template>
